import React, { useMemo, useState } from 'react';
import { CircularProgress, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { formatPrice } from '../../../../appointments/v2/utils/appointmentUtils';
import InsuranceProcedureFormPopup from './InsuranceProcedureFormPopup';
import styles from './InsuranceProcedures.module.css';

const SEARCH_FROM = 8;

const normalize = (value) => String(value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR', { sensitivity: 'base' });

/** Copiar a tabela de outra operadora para esta (códigos já presentes ficam como estão). */
function CopyFromProvider({ provider, providers, onCopied, onCancel }) {
    const { CopyInsuranceProcedures } = DataProviderInstance();
    const sources = providers.filter((item) =>
        String(item.id) !== String(provider.id) && (item.procedures || []).some((p) => Number(p.active) === 1));
    const [sourceId, setSourceId] = useState(sources[0] ? String(sources[0].id) : '');
    const [copying, setCopying] = useState(false);
    const [error, setError] = useState('');

    if (sources.length === 0) {
        return (
            <div className={styles.copyBox}>
                <span className={styles.hint}>Nenhuma outra operadora tem tabela cadastrada ainda.</span>
                <CustomButton style="primary" variant="text" label="Fechar" onClick={onCancel} />
            </div>
        );
    }

    const copy = () => {
        setCopying(true);
        setError('');
        CopyInsuranceProcedures(sourceId, provider.id, (result) => {
            setCopying(false);
            if (result?.status === 'SUCCESS') {
                const { copied = 0, skipped = 0 } = result.data || {};
                const parts = [`${copied} ${copied === 1 ? 'procedimento copiado' : 'procedimentos copiados'}`];
                if (skipped > 0) parts.push(`${skipped} já ${skipped === 1 ? 'estava' : 'estavam'} na tabela`);
                onCopied(parts.join(', '));
            } else {
                setError(result?.error || 'Não foi possível copiar. Tente de novo.');
            }
        });
    };

    return (
        <div className={styles.copyBox}>
            <div className={styles.copyRow}>
                <TextField
                    select
                    size="small"
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                    disabled={copying}
                    className={`${styles.input} ${styles.copySelect}`}
                    inputProps={{ 'aria-label': 'Copiar de' }}
                >
                    {sources.map((item) => {
                        const count = (item.procedures || []).filter((p) => Number(p.active) === 1).length;
                        return (
                            <MenuItem key={item.id} value={String(item.id)}>
                                {item.name} ({count} {count === 1 ? 'procedimento' : 'procedimentos'})
                            </MenuItem>
                        );
                    })}
                </TextField>
                <CustomButton style="primary" variant="text" label="Cancelar" onClick={onCancel} enabled={!copying} />
                <CustomButton
                    style="main"
                    variant="contained"
                    label="Copiar"
                    icon={copying ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={copy}
                    enabled={!copying && !!sourceId}
                />
            </div>
            <span className={styles.hint}>
                Vêm códigos, nomes, valores e o vínculo com a agenda. Códigos que já estão aqui ficam como estão; depois é só ajustar os valores que mudam.
            </span>
            {error && <span className={styles.errorText} role="alert">{error}</span>}
        </div>
    );
}

/** Uma linha da tabela: código, nome, procedimento da agenda, valor e ações. */
function ProcedureRow({ procedure, agendaLabel, canEdit, busy, onEdit, onToggle }) {
    const inactive = Number(procedure.active) !== 1;
    return (
        <li className={`${styles.row} ${inactive ? styles.muted : ''}`}>
            <span className={styles.code}>{procedure.code}</span>
            <div className={styles.identity}>
                <span className={styles.name}>{procedure.name}</span>
                <span className={styles.secondary}>
                    {agendaLabel ? `Na agenda: ${agendaLabel}` : 'Sem procedimento da agenda'}
                    {!procedure.tuss_code && <span className={styles.tag}>Próprio</span>}
                    {inactive && <span className={`${styles.tag} ${styles.tagInactive}`}>Inativo</span>}
                </span>
            </div>
            <span className={styles.price}>{procedure.price != null ? formatPrice(Number(procedure.price)) : 'Sem valor'}</span>
            {canEdit && (
                <span className={styles.actions}>
                    <Tooltip title="Editar" arrow>
                        <span>
                            <IconButton size="small" disabled={busy} onClick={() => onEdit(procedure)} aria-label={`Editar ${procedure.code}`}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={inactive ? 'Ativar' : 'Inativar'} arrow>
                        <span>
                            <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => onToggle(procedure)}
                                aria-label={`${inactive ? 'Ativar' : 'Inativar'} ${procedure.code}`}
                            >
                                {inactive ? <RestoreOutlinedIcon fontSize="small" /> : <PowerSettingsNewOutlinedIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </span>
            )}
        </li>
    );
}

/**
 * Tabela de procedimentos de uma operadora: códigos (da TUSS ou próprios), nomes e
 * valores de contrato desta conta. Só proprietário e administradores alteram; os
 * demais consultam. Nunca exclui: inativo some do agendamento e o histórico fica.
 */
function InsuranceProceduresPopup({ provider, providers, canEdit, onChanged, onClose }) {
    const { CompanyConfig, UpdateInsuranceProcedure } = DataProviderInstance();
    const [editing, setEditing] = useState(null);
    const [copyOpen, setCopyOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');

    const procedures = useMemo(() => provider.procedures || [], [provider.procedures]);
    const agendaById = useMemo(() => {
        const map = {};
        (CompanyConfig?.Procedures || []).forEach((item) => { map[String(item.Id)] = item.Label; });
        return map;
    }, [CompanyConfig?.Procedures]);

    const visible = useMemo(() => {
        const query = normalize(search.trim());
        const list = query
            ? procedures.filter((p) => normalize(p.code).includes(query) || normalize(p.name).includes(query))
            : procedures;
        const active = list.filter((p) => Number(p.active) === 1).sort(byName);
        const inactive = list.filter((p) => Number(p.active) !== 1).sort(byName);
        return [...active, ...inactive];
    }, [procedures, search]);

    const activeCount = procedures.filter((p) => Number(p.active) === 1).length;

    const toggle = (procedure) => {
        setBusy(true);
        setError('');
        const nextActive = Number(procedure.active) === 1 ? 0 : 1;
        UpdateInsuranceProcedure({
            id: procedure.id,
            code: procedure.code,
            name: procedure.name,
            price: procedure.price ?? '',
            procedure_id: procedure.procedure_id ?? '',
            active: nextActive
        }, (result) => {
            setBusy(false);
            if (result?.status === 'SUCCESS') {
                onChanged(nextActive ? 'Procedimento ativado' : 'Procedimento inativado');
            } else {
                setError(result?.error || 'Não foi possível mudar a situação. Tente de novo.');
            }
        });
    };

    const content = (
        <div className={styles.content}>
            <p className={styles.intro}>
                Os códigos e valores que você usa com a {provider.name}. No atendimento por convênio, cada procedimento
                mostra o código e o valor a receber da operadora. Ficam só na sua conta.
            </p>

            {canEdit && (
                <div className={styles.toolbar}>
                    <CustomButton
                        style="main"
                        variant="contained"
                        icon={<AddRoundedIcon />}
                        label="Novo procedimento"
                        onClick={() => setEditing({})}
                        enabled={!busy}
                    />
                    <CustomButton
                        style="secondary"
                        variant="outlined"
                        icon={<ContentCopyOutlinedIcon />}
                        label="Copiar de outra operadora"
                        onClick={() => setCopyOpen(true)}
                        enabled={!busy && !copyOpen}
                    />
                </div>
            )}

            {copyOpen && (
                <CopyFromProvider
                    provider={provider}
                    providers={providers}
                    onCancel={() => setCopyOpen(false)}
                    onCopied={(message) => { setCopyOpen(false); onChanged(message); }}
                />
            )}

            {error && <div className={styles.errorText} role="alert">{error}</div>}

            {procedures.length === 0 ? (
                <div className={styles.empty}>
                    <span className={styles.emptyIcon}><ListAltOutlinedIcon /></span>
                    <span className={styles.emptyTitle}>Nenhum procedimento nesta tabela</span>
                    <span className={styles.hint}>
                        {canEdit
                            ? 'Busque na TUSS ou copie a tabela de outra operadora e ajuste os valores.'
                            : 'Proprietários e administradores cadastram os códigos e valores desta operadora.'}
                    </span>
                </div>
            ) : (
                <>
                    <div className={styles.listHeader}>
                        <span className={styles.count}>
                            {activeCount} {activeCount === 1 ? 'procedimento ativo' : 'procedimentos ativos'}
                        </span>
                        {procedures.length >= SEARCH_FROM && (
                            <TextField
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar por código ou nome"
                                className={`${styles.input} ${styles.search}`}
                                inputProps={{ 'aria-label': 'Buscar na tabela' }}
                            />
                        )}
                    </div>
                    {visible.length === 0 ? (
                        <span className={styles.hint}>Nada encontrado.</span>
                    ) : (
                        <ul className={styles.list}>
                            {visible.map((procedure) => (
                                <ProcedureRow
                                    key={procedure.id}
                                    procedure={procedure}
                                    agendaLabel={procedure.procedure_id != null ? agendaById[String(procedure.procedure_id)] : null}
                                    canEdit={canEdit}
                                    busy={busy}
                                    onEdit={setEditing}
                                    onToggle={toggle}
                                />
                            ))}
                        </ul>
                    )}
                </>
            )}

            {editing && (
                <InsuranceProcedureFormPopup
                    providerId={provider.id}
                    providerName={provider.name}
                    procedure={editing.id ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={(message) => { setEditing(null); onChanged(message); }}
                />
            )}
        </div>
    );

    return (
        <PopupBase
            Title={`Tabela de procedimentos · ${provider.name}`}
            Content={content}
            OnClose={onClose}
            maxWidth="md"
        />
    );
}

export default InsuranceProceduresPopup;
